/**
 * CodeDisplay Web Component
 * A syntax-highlighted code viewer that loads external files or displays inline code
 * 
 * This version bundles highlight.js and popular themes for offline use.
 * 
 * Usage with external file:
 *   <code-display 
 *     src="example.js"
 *     language="javascript"
 *     theme="github-dark"
 *     line-numbers
 *     copy-button>
 *   </code-display>
 * 
 * Usage with inline code:
 *   <code-display language="javascript" line-numbers>
 *     function hello() {
 *       console.log("Hello, World!");
 *     }
 *   </code-display>
 * 
 * Usage with inline HTML (use escaped entities):
 *   <code-display language="html" line-numbers>
 *     &lt;div class="container"&gt;
 *       &lt;h1&gt;Hello&lt;/h1&gt;
 *     &lt;/div&gt;
 *   </code-display>
 * 
 * Attributes:
 *   src           - URL of the file to load (omit for inline content)
 *   language      - Language for syntax highlighting (auto-detected from src extension if omitted)
 *   theme         - Theme name (see THEMES object for available themes)
 *   tab-size      - Number of spaces per tab (default: 4)
 *   line-numbers  - Show line numbers (boolean attribute)
 *   gutter-width  - Number of digits for line number gutter (default: auto-sized)
 *   show-lines    - Only show specified lines, e.g. "30-55" or "1-10,20-30" (shows all if omitted)
 *   highlight-lines - Lines to highlight, e.g. "1-3,7,10-15"
 *   start-line    - Starting line number for display (default: 1)
 *   max-height    - Maximum height before scrolling (default: none)
 *   collapsible   - Allow collapsing the code block (boolean attribute)
 *   collapsed     - Start in collapsed state (boolean attribute)
 *   copy-button   - Show copy to clipboard button (boolean attribute)
 *   title         - Custom title (defaults to filename or "Code")
 *   wrap          - Wrap long lines instead of horizontal scroll (boolean attribute)
 * 
 * Events:
 *   code-loaded   - Fired when code is successfully loaded from URL
 *   code-error    - Fired when loading fails
 *   code-copied   - Fired when code is copied to clipboard
 * 
 * Public API:
 *   loadFile(url)      - Load a different file
 *   setCode(code, lang) - Set code content directly
 *   getCode()          - Get the current code content
 *   copyToClipboard()  - Copy code to clipboard
 *   expand()           - Expand if collapsible
 *   collapse()         - Collapse if collapsible
 *   refresh()          - Re-render with current settings
 *   static getAvailableThemes() - Get list of available theme names
 * 
 * Inline Content Notes:
 *   - HTML must be escaped using entities (&lt; &gt; &amp;) to prevent browser parsing
 *   - Indentation is based on the first line: its leading whitespace is removed from all lines
 *   - Leading/trailing blank lines are trimmed
 */

import hljs from 'highlight.js';

// Extension to language mapping
const EXTENSION_MAP = {
  // JavaScript family
  'js': 'javascript',
  'mjs': 'javascript',
  'cjs': 'javascript',
  'jsx': 'javascript',
  'ts': 'typescript',
  'tsx': 'typescript',

  // Web
  'html': 'html',
  'htm': 'html',
  'xhtml': 'html',
  'css': 'css',
  'scss': 'scss',
  'sass': 'scss',
  'less': 'less',
  'svg': 'xml',

  // Data formats
  'json': 'json',
  'xml': 'xml',
  'yaml': 'yaml',
  'yml': 'yaml',
  'toml': 'ini',
  'ini': 'ini',
  'csv': 'plaintext',

  // Programming languages
  'py': 'python',
  'python': 'python',
  'rb': 'ruby',
  'ruby': 'ruby',
  'java': 'java',
  'kt': 'kotlin',
  'kts': 'kotlin',
  'scala': 'scala',
  'go': 'go',
  'rs': 'rust',
  'c': 'c',
  'h': 'c',
  'cpp': 'cpp',
  'cc': 'cpp',
  'cxx': 'cpp',
  'hpp': 'cpp',
  'hxx': 'cpp',
  'cs': 'csharp',
  'swift': 'swift',
  'php': 'php',
  'pl': 'perl',
  'pm': 'perl',
  'lua': 'lua',
  'r': 'r',
  'R': 'r',
  'jl': 'julia',
  'ex': 'elixir',
  'exs': 'elixir',
  'erl': 'erlang',
  'hrl': 'erlang',
  'hs': 'haskell',
  'lhs': 'haskell',
  'ml': 'ocaml',
  'mli': 'ocaml',
  'fs': 'fsharp',
  'fsx': 'fsharp',
  'clj': 'clojure',
  'cljs': 'clojure',
  'cljc': 'clojure',
  'lisp': 'lisp',
  'el': 'lisp',
  'scm': 'scheme',
  'rkt': 'scheme',
  'zig': 'zig',
  'nim': 'nim',
  'v': 'v',
  'd': 'd',
  'dart': 'dart',
  'groovy': 'groovy',
  'gradle': 'groovy',

  // Shell & scripting
  'sh': 'bash',
  'bash': 'bash',
  'zsh': 'bash',
  'fish': 'bash',
  'ps1': 'powershell',
  'psm1': 'powershell',
  'bat': 'dos',
  'cmd': 'dos',
  'applescript': 'applescript',
  'scpt': 'applescript',
  'scptd': 'applescript',

  // Markup & documentation
  'md': 'markdown',
  'markdown': 'markdown',
  'rst': 'plaintext',
  'tex': 'latex',
  'latex': 'latex',
  'bib': 'bibtex',

  // Config files
  'dockerfile': 'dockerfile',
  'makefile': 'makefile',
  'cmake': 'cmake',
  'nginx': 'nginx',
  'apache': 'apache',
  'htaccess': 'apache',

  // Database
  'sql': 'sql',
  'mysql': 'sql',
  'pgsql': 'sql',
  'plsql': 'sql',

  // Other
  'diff': 'diff',
  'patch': 'diff',
  'asm': 'x86asm',
  's': 'x86asm',
  'wasm': 'wasm',
  'proto': 'protobuf',
  'graphql': 'graphql',
  'gql': 'graphql',
  'vue': 'html',
  'svelte': 'html',
};

// =============================================================================
// BUNDLED HIGHLIGHT.JS THEMES
// These are the 20 most popular themes, embedded as CSS strings
// =============================================================================

const HLJS_THEMES = {
  // ---------------------------------------------------------------------------
  // DARK THEMES
  // ---------------------------------------------------------------------------
  
  'atom-one-dark': `
    .hljs{color:#abb2bf;background:#282c34}
    .hljs-comment,.hljs-quote{color:#5c6370;font-style:italic}
    .hljs-doctag,.hljs-keyword,.hljs-formula{color:#c678dd}
    .hljs-section,.hljs-name,.hljs-selector-tag,.hljs-deletion,.hljs-subst{color:#e06c75}
    .hljs-literal{color:#56b6c2}
    .hljs-string,.hljs-regexp,.hljs-addition,.hljs-attribute,.hljs-meta .hljs-string{color:#98c379}
    .hljs-attr,.hljs-variable,.hljs-template-variable,.hljs-type,.hljs-selector-class,.hljs-selector-attr,.hljs-selector-pseudo,.hljs-number{color:#d19a66}
    .hljs-symbol,.hljs-bullet,.hljs-link,.hljs-meta,.hljs-selector-id,.hljs-title{color:#61aeee}
    .hljs-built_in,.hljs-title.class_,.hljs-class .hljs-title{color:#e6c07b}
    .hljs-emphasis{font-style:italic}
    .hljs-strong{font-weight:700}
    .hljs-link{text-decoration:underline}
  `,
  
  'github-dark': `
    .hljs{color:#c9d1d9;background:#0d1117}
    .hljs-doctag,.hljs-keyword,.hljs-meta .hljs-keyword,.hljs-template-tag,.hljs-template-variable,.hljs-type,.hljs-variable.language_{color:#ff7b72}
    .hljs-title,.hljs-title.class_,.hljs-title.class_.inherited__,.hljs-title.function_{color:#d2a8ff}
    .hljs-attr,.hljs-attribute,.hljs-literal,.hljs-meta,.hljs-number,.hljs-operator,.hljs-selector-attr,.hljs-selector-class,.hljs-selector-id,.hljs-variable{color:#79c0ff}
    .hljs-meta .hljs-string,.hljs-regexp,.hljs-string{color:#a5d6ff}
    .hljs-built_in,.hljs-symbol{color:#ffa657}
    .hljs-code,.hljs-comment,.hljs-formula{color:#8b949e}
    .hljs-name,.hljs-quote,.hljs-selector-pseudo,.hljs-selector-tag{color:#7ee787}
    .hljs-subst{color:#c9d1d9}
    .hljs-section{color:#1f6feb;font-weight:700}
    .hljs-bullet{color:#f2cc60}
    .hljs-emphasis{color:#c9d1d9;font-style:italic}
    .hljs-strong{color:#c9d1d9;font-weight:700}
    .hljs-addition{color:#aff5b4;background-color:#033a16}
    .hljs-deletion{color:#ffdcd7;background-color:#67060c}
  `,
  
  'github-dark-dimmed': `
    .hljs{color:#adbac7;background:#22272e}
    .hljs-doctag,.hljs-keyword,.hljs-meta .hljs-keyword,.hljs-template-tag,.hljs-template-variable,.hljs-type,.hljs-variable.language_{color:#f47067}
    .hljs-title,.hljs-title.class_,.hljs-title.class_.inherited__,.hljs-title.function_{color:#dcbdfb}
    .hljs-attr,.hljs-attribute,.hljs-literal,.hljs-meta,.hljs-number,.hljs-operator,.hljs-selector-attr,.hljs-selector-class,.hljs-selector-id,.hljs-variable{color:#6cb6ff}
    .hljs-meta .hljs-string,.hljs-regexp,.hljs-string{color:#96d0ff}
    .hljs-built_in,.hljs-symbol{color:#f69d50}
    .hljs-code,.hljs-comment,.hljs-formula{color:#768390}
    .hljs-name,.hljs-quote,.hljs-selector-pseudo,.hljs-selector-tag{color:#8ddb8c}
    .hljs-subst{color:#adbac7}
    .hljs-section{color:#316dca;font-weight:700}
    .hljs-bullet{color:#eac55f}
    .hljs-emphasis{color:#adbac7;font-style:italic}
    .hljs-strong{color:#adbac7;font-weight:700}
    .hljs-addition{color:#b4f1b4;background-color:#1b4721}
    .hljs-deletion{color:#ffd8d3;background-color:#78191b}
  `,
  
  'monokai': `
    .hljs{background:#272822;color:#ddd}
    .hljs-tag,.hljs-keyword,.hljs-selector-tag,.hljs-literal,.hljs-strong,.hljs-name{color:#f92672}
    .hljs-code{color:#66d9ef}
    .hljs-attribute,.hljs-symbol,.hljs-regexp,.hljs-link{color:#bf79db}
    .hljs-string,.hljs-bullet,.hljs-subst,.hljs-title,.hljs-section,.hljs-emphasis,.hljs-type,.hljs-built_in,.hljs-selector-attr,.hljs-selector-pseudo,.hljs-addition,.hljs-variable,.hljs-template-tag,.hljs-template-variable{color:#a6e22e}
    .hljs-title.class_{color:#fff}
    .hljs-comment,.hljs-quote,.hljs-deletion,.hljs-meta{color:#75715e}
    .hljs-keyword,.hljs-selector-tag,.hljs-literal,.hljs-doctag,.hljs-title,.hljs-section,.hljs-type,.hljs-selector-id{font-weight:700}
  `,
  
  'monokai-sublime': `
    .hljs{background:#23241f;color:#f8f8f2}
    .hljs-tag,.hljs-subst{color:#f8f8f2}
    .hljs-emphasis,.hljs-strong{color:#a8a8a2}
    .hljs-bullet,.hljs-quote,.hljs-link,.hljs-number,.hljs-regexp,.hljs-literal{color:#ae81ff}
    .hljs-code,.hljs-title,.hljs-section,.hljs-selector-class{color:#a6e22e}
    .hljs-strong{font-weight:700}
    .hljs-emphasis{font-style:italic}
    .hljs-keyword,.hljs-selector-tag,.hljs-name{color:#f92672}
    .hljs-attr,.hljs-symbol,.hljs-attribute{color:#66d9ef}
    .hljs-params,.hljs-class .hljs-title,.hljs-title.class_{color:#f8f8f2}
    .hljs-string,.hljs-type,.hljs-built_in,.hljs-selector-id,.hljs-selector-attr,.hljs-selector-pseudo,.hljs-addition,.hljs-variable,.hljs-template-variable{color:#e6db74}
    .hljs-comment,.hljs-deletion,.hljs-meta{color:#75715e}
  `,
  
  'dracula': `
    .hljs{background:#282a36;color:#f8f8f2}
    .hljs-keyword,.hljs-selector-tag,.hljs-literal,.hljs-section,.hljs-link{color:#8be9fd}
    .hljs-function .hljs-keyword{color:#ff79c6}
    .hljs-subst,.hljs-string,.hljs-attribute,.hljs-symbol,.hljs-bullet,.hljs-built_in,.hljs-addition,.hljs-variable,.hljs-template-tag,.hljs-template-variable{color:#f1fa8c}
    .hljs-title,.hljs-title.class_,.hljs-title.class_.inherited__,.hljs-title.function_{color:#50fa7b}
    .hljs-comment,.hljs-quote,.hljs-deletion,.hljs-meta{color:#6272a4}
    .hljs-keyword,.hljs-selector-tag,.hljs-literal,.hljs-title,.hljs-section,.hljs-doctag,.hljs-type,.hljs-name,.hljs-strong{font-weight:700}
    .hljs-emphasis{font-style:italic}
  `,
  
  'nord': `
    .hljs{background:#2e3440;color:#d8dee9}
    .hljs-subst{color:#d8dee9}
    .hljs-selector-tag{color:#81a1c1}
    .hljs-selector-id{color:#8fbcbb;font-weight:700}
    .hljs-selector-class{color:#8fbcbb}
    .hljs-property,.hljs-selector-attr,.hljs-selector-pseudo,.hljs-regexp,.hljs-link,.hljs-literal,.hljs-bullet{color:#88c0d0}
    .hljs-built_in,.hljs-type,.hljs-template-variable{color:#8fbcbb}
    .hljs-deletion{color:#bf616a}
    .hljs-title,.hljs-section,.hljs-title.class_,.hljs-title.class_.inherited__,.hljs-title.function_{color:#88c0d0}
    .hljs-tag,.hljs-name{color:#81a1c1}
    .hljs-string,.hljs-number,.hljs-addition,.hljs-attribute,.hljs-symbol,.hljs-template-tag{color:#a3be8c}
    .hljs-variable{color:#d8dee9}
    .hljs-keyword,.hljs-meta{color:#81a1c1}
    .hljs-comment{color:#4c566a}
    .hljs-doctag{color:#8fbcbb}
    .hljs-code,.hljs-quote,.hljs-formula{color:#8fbcbb}
    .hljs-emphasis{font-style:italic}
    .hljs-strong{font-weight:700}
  `,
  
  'tokyo-night-dark': `
    .hljs{background:#1a1b26;color:#9aa5ce}
    .hljs-comment,.hljs-meta{color:#565f89}
    .hljs-tag,.hljs-doctag,.hljs-selector-id,.hljs-selector-class,.hljs-selector-attr,.hljs-selector-pseudo{color:#f7768e}
    .hljs-attribute{color:#bb9af7}
    .hljs-keyword,.hljs-type,.hljs-selector-tag{color:#bb9af7}
    .hljs-code,.hljs-title,.hljs-title.class_,.hljs-title.function_{color:#7aa2f7}
    .hljs-string,.hljs-regexp,.hljs-symbol,.hljs-link,.hljs-template-tag,.hljs-template-variable,.hljs-addition{color:#9ece6a}
    .hljs-number,.hljs-built_in,.hljs-literal,.hljs-variable,.hljs-attr{color:#ff9e64}
    .hljs-bullet,.hljs-subst,.hljs-quote{color:#73daca}
    .hljs-section{color:#7dcfff;font-weight:700}
    .hljs-name{color:#73daca}
    .hljs-deletion{color:#f7768e}
    .hljs-emphasis{font-style:italic}
    .hljs-strong{font-weight:700}
  `,
  
  'vs2015': `
    .hljs{background:#1e1e1e;color:#dcdcdc}
    .hljs-keyword,.hljs-literal,.hljs-symbol,.hljs-name{color:#569cd6}
    .hljs-link{color:#569cd6;text-decoration:underline}
    .hljs-built_in,.hljs-type{color:#4ec9b0}
    .hljs-class,.hljs-number{color:#b8d7a3}
    .hljs-string,.hljs-meta .hljs-string{color:#d69d85}
    .hljs-regexp,.hljs-template-tag{color:#9a5334}
    .hljs-formula,.hljs-function,.hljs-params,.hljs-subst,.hljs-title{color:#dcdcdc}
    .hljs-comment,.hljs-quote{color:#57a64a;font-style:italic}
    .hljs-doctag{color:#608b4e}
    .hljs-section,.hljs-tag,.hljs-selector-id,.hljs-selector-class,.hljs-selector-attr,.hljs-selector-pseudo{color:#9cdcfe}
    .hljs-meta,.hljs-attr{color:#9cdcfe}
    .hljs-attribute{color:#b8d7a3}
    .hljs-variable,.hljs-template-variable{color:#bd63c5}
    .hljs-bullet{color:#d4d4d4}
    .hljs-addition{color:#b5cea8}
    .hljs-deletion{color:#ce9178}
    .hljs-emphasis{font-style:italic}
    .hljs-strong{font-weight:700}
  `,
  
  'one-dark': `
    .hljs{background:#282c34;color:#abb2bf}
    .hljs-keyword,.hljs-operator,.hljs-pattern-match{color:#f92672}
    .hljs-function,.hljs-pattern-match .hljs-constructor{color:#61aeee}
    .hljs-function .hljs-params{color:#a6e22e}
    .hljs-function .hljs-params .hljs-typing{color:#fd971f}
    .hljs-module-access .hljs-module{color:#7e57c2}
    .hljs-constructor{color:#e2b93d}
    .hljs-constructor .hljs-string{color:#9ccc65}
    .hljs-comment,.hljs-quote{color:#b18eb1;font-style:italic}
    .hljs-doctag,.hljs-formula{color:#c678dd}
    .hljs-deletion,.hljs-name,.hljs-section,.hljs-selector-tag,.hljs-subst{color:#e06c75}
    .hljs-literal{color:#56b6c2}
    .hljs-addition,.hljs-attribute,.hljs-meta .hljs-string,.hljs-regexp,.hljs-string{color:#98c379}
    .hljs-built_in,.hljs-class .hljs-title,.hljs-title.class_{color:#e6c07b}
    .hljs-attr,.hljs-number,.hljs-selector-attr,.hljs-selector-class,.hljs-selector-pseudo,.hljs-template-variable,.hljs-type,.hljs-variable{color:#d19a66}
    .hljs-bullet,.hljs-link,.hljs-meta,.hljs-selector-id,.hljs-symbol,.hljs-title{color:#61aeee}
    .hljs-emphasis{font-style:italic}
    .hljs-strong{font-weight:700}
    .hljs-link{text-decoration:underline}
  `,

  'obsidian': `
    .hljs{background:#282b2e;color:#e0e2e4}
    .hljs-keyword,.hljs-literal,.hljs-symbol,.hljs-name{color:#93c763}
    .hljs-link{color:#93c763;text-decoration:underline}
    .hljs-built_in,.hljs-type{color:#8cbbad}
    .hljs-number,.hljs-class{color:#ffcd22}
    .hljs-string,.hljs-meta .hljs-string{color:#ec7600}
    .hljs-regexp,.hljs-template-tag{color:#ec7600}
    .hljs-formula,.hljs-function,.hljs-params,.hljs-subst,.hljs-title{color:#e0e2e4}
    .hljs-comment,.hljs-quote{color:#818e96;font-style:italic}
    .hljs-doctag{color:#557182}
    .hljs-section,.hljs-tag,.hljs-selector-id,.hljs-selector-class,.hljs-selector-attr,.hljs-selector-pseudo{color:#8cbbad}
    .hljs-attr,.hljs-attribute,.hljs-variable,.hljs-template-variable,.hljs-meta{color:#8cbbad}
    .hljs-bullet{color:#e0e2e4}
    .hljs-addition{color:#8cbbad}
    .hljs-deletion{color:#d39745}
    .hljs-emphasis{font-style:italic}
    .hljs-strong{font-weight:700}
  `,
  
  // ---------------------------------------------------------------------------
  // LIGHT THEMES
  // ---------------------------------------------------------------------------
  
  'atom-one-light': `
    .hljs{color:#383a42;background:#fafafa}
    .hljs-comment,.hljs-quote{color:#a0a1a7;font-style:italic}
    .hljs-doctag,.hljs-keyword,.hljs-formula{color:#a626a4}
    .hljs-section,.hljs-name,.hljs-selector-tag,.hljs-deletion,.hljs-subst{color:#e45649}
    .hljs-literal{color:#0184bb}
    .hljs-string,.hljs-regexp,.hljs-addition,.hljs-attribute,.hljs-meta .hljs-string{color:#50a14f}
    .hljs-attr,.hljs-variable,.hljs-template-variable,.hljs-type,.hljs-selector-class,.hljs-selector-attr,.hljs-selector-pseudo,.hljs-number{color:#986801}
    .hljs-symbol,.hljs-bullet,.hljs-link,.hljs-meta,.hljs-selector-id,.hljs-title{color:#4078f2}
    .hljs-built_in,.hljs-title.class_,.hljs-class .hljs-title{color:#c18401}
    .hljs-emphasis{font-style:italic}
    .hljs-strong{font-weight:700}
    .hljs-link{text-decoration:underline}
  `,
  
  'github': `
    .hljs{color:#24292e;background:#fff}
    .hljs-doctag,.hljs-keyword,.hljs-meta .hljs-keyword,.hljs-template-tag,.hljs-template-variable,.hljs-type,.hljs-variable.language_{color:#d73a49}
    .hljs-title,.hljs-title.class_,.hljs-title.class_.inherited__,.hljs-title.function_{color:#6f42c1}
    .hljs-attr,.hljs-attribute,.hljs-literal,.hljs-meta,.hljs-number,.hljs-operator,.hljs-selector-attr,.hljs-selector-class,.hljs-selector-id,.hljs-variable{color:#005cc5}
    .hljs-meta .hljs-string,.hljs-regexp,.hljs-string{color:#032f62}
    .hljs-built_in,.hljs-symbol{color:#e36209}
    .hljs-code,.hljs-comment,.hljs-formula{color:#6a737d}
    .hljs-name,.hljs-quote,.hljs-selector-pseudo,.hljs-selector-tag{color:#22863a}
    .hljs-subst{color:#24292e}
    .hljs-section{color:#005cc5;font-weight:700}
    .hljs-bullet{color:#735c0f}
    .hljs-emphasis{color:#24292e;font-style:italic}
    .hljs-strong{color:#24292e;font-weight:700}
    .hljs-addition{color:#22863a;background-color:#f0fff4}
    .hljs-deletion{color:#b31d28;background-color:#ffeef0}
  `,
  
  'vs': `
    .hljs{background:#fff;color:#000}
    .hljs-comment,.hljs-quote,.hljs-variable{color:#008000}
    .hljs-keyword,.hljs-selector-tag,.hljs-built_in,.hljs-name,.hljs-tag{color:#00f}
    .hljs-string,.hljs-title,.hljs-section,.hljs-attribute,.hljs-literal,.hljs-template-tag,.hljs-template-variable,.hljs-type,.hljs-addition{color:#a31515}
    .hljs-deletion,.hljs-selector-attr,.hljs-selector-pseudo,.hljs-meta{color:#2b91af}
    .hljs-doctag{color:#808080}
    .hljs-attr{color:#f00}
    .hljs-symbol,.hljs-bullet,.hljs-link{color:#00b0e8}
    .hljs-emphasis{font-style:italic}
    .hljs-strong{font-weight:700}
  `,
  
  'xcode': `
    .hljs{background:#fff;color:#000}
    .hljs-comment,.hljs-quote{color:#006a00}
    .hljs-keyword,.hljs-literal,.hljs-selector-tag,.hljs-type{color:#aa0d91}
    .hljs-template-tag,.hljs-template-variable{color:#3f6e74}
    .hljs-name,.hljs-attribute{color:#836c28}
    .hljs-number,.hljs-built_in{color:#1c00cf}
    .hljs-string,.hljs-symbol,.hljs-link,.hljs-addition{color:#c41a16}
    .hljs-section,.hljs-title{color:#643820}
    .hljs-regexp,.hljs-meta,.hljs-deletion{color:#c41a16}
    .hljs-variable,.hljs-bullet,.hljs-subst,.hljs-selector-attr,.hljs-selector-pseudo,.hljs-selector-id{color:#5c2699}
    .hljs-tag,.hljs-selector-class{color:#3f6e74}
    .hljs-doctag{font-weight:700}
    .hljs-emphasis{font-style:italic}
    .hljs-strong{font-weight:700}
  `,
  
  'intellij-light': `
    .hljs{background:#fff;color:#000}
    .hljs-subst,.hljs-title{color:#000;font-weight:400}
    .hljs-comment,.hljs-quote{color:#8c8c8c;font-style:italic}
    .hljs-meta{color:#9e880d}
    .hljs-tag{background:#efefef}
    .hljs-section,.hljs-name,.hljs-literal,.hljs-keyword,.hljs-selector-tag,.hljs-type,.hljs-selector-id,.hljs-selector-class{color:#000080;font-weight:700}
    .hljs-attribute{color:#0000ff}
    .hljs-number,.hljs-regexp,.hljs-link{color:#0000cd}
    .hljs-string,.hljs-addition{color:#008000;font-weight:700}
    .hljs-symbol,.hljs-template-tag,.hljs-template-variable{color:#660e7a}
    .hljs-built_in{color:#008080}
    .hljs-deletion,.hljs-bullet,.hljs-selector-attr,.hljs-selector-pseudo{color:#000}
    .hljs-formula{background:#eee;font-style:italic}
    .hljs-variable{color:#660e7a}
    .hljs-emphasis{font-style:italic}
    .hljs-strong{font-weight:700}
  `,
  
  'stackoverflow-light': `
    .hljs{background:#f6f6f6;color:#2f3337}
    .hljs-subst{color:#2f3337}
    .hljs-comment{color:#656e77}
    .hljs-attr,.hljs-doctag,.hljs-keyword,.hljs-meta .hljs-keyword,.hljs-section,.hljs-selector-tag{color:#015692}
    .hljs-attribute{color:#803378}
    .hljs-literal,.hljs-name,.hljs-number,.hljs-selector-id,.hljs-selector-class,.hljs-template-variable,.hljs-type,.hljs-variable.language_{color:#b75501}
    .hljs-string,.hljs-meta .hljs-string,.hljs-regexp{color:#54790d}
    .hljs-built_in,.hljs-symbol{color:#b75501}
    .hljs-code,.hljs-formula,.hljs-quote{color:#535a60}
    .hljs-punctuation,.hljs-selector-attr,.hljs-selector-pseudo{color:#535a60}
    .hljs-bullet,.hljs-link,.hljs-meta,.hljs-operator,.hljs-title,.hljs-title.class_,.hljs-title.class_.inherited__,.hljs-title.function_{color:#015692}
    .hljs-deletion{color:#c02d2e}
    .hljs-addition{color:#2f6f44}
    .hljs-tag{color:#015692}
    .hljs-emphasis{font-style:italic}
    .hljs-strong{font-weight:700}
  `,
  
  'stackoverflow-dark': `
    .hljs{background:#1c1b1b;color:#c8ced3}
    .hljs-subst{color:#c8ced3}
    .hljs-comment{color:#999}
    .hljs-attr,.hljs-doctag,.hljs-keyword,.hljs-meta .hljs-keyword,.hljs-section,.hljs-selector-tag{color:#88aece}
    .hljs-attribute{color:#c59bc1}
    .hljs-literal,.hljs-name,.hljs-number,.hljs-selector-id,.hljs-selector-class,.hljs-template-variable,.hljs-type,.hljs-variable.language_{color:#f08d49}
    .hljs-string,.hljs-meta .hljs-string,.hljs-regexp{color:#b5bd68}
    .hljs-built_in,.hljs-symbol{color:#f08d49}
    .hljs-code,.hljs-formula,.hljs-quote{color:#999}
    .hljs-punctuation,.hljs-selector-attr,.hljs-selector-pseudo{color:#999}
    .hljs-bullet,.hljs-link,.hljs-meta,.hljs-operator,.hljs-title,.hljs-title.class_,.hljs-title.class_.inherited__,.hljs-title.function_{color:#88aece}
    .hljs-deletion{color:#de7176}
    .hljs-addition{color:#76c490}
    .hljs-tag{color:#88aece}
    .hljs-emphasis{font-style:italic}
    .hljs-strong{font-weight:700}
  `,
  
  'a11y-light': `
    .hljs{background:#fefefe;color:#545454}
    .hljs-comment,.hljs-quote{color:#696969}
    .hljs-deletion,.hljs-name,.hljs-regexp,.hljs-selector-class,.hljs-selector-id,.hljs-tag,.hljs-template-variable,.hljs-variable{color:#d91e18}
    .hljs-attribute,.hljs-built_in,.hljs-link,.hljs-literal,.hljs-meta,.hljs-number,.hljs-params,.hljs-type{color:#aa5d00}
    .hljs-addition,.hljs-bullet,.hljs-string,.hljs-symbol{color:green}
    .hljs-keyword,.hljs-selector-tag,.hljs-section,.hljs-title{color:#007faa}
    .hljs-emphasis{font-style:italic}
    .hljs-strong{font-weight:700}
  `,
  
  'a11y-dark': `
    .hljs{background:#2b2b2b;color:#f8f8f2}
    .hljs-comment,.hljs-quote{color:#d4d0ab}
    .hljs-deletion,.hljs-name,.hljs-regexp,.hljs-selector-class,.hljs-selector-id,.hljs-tag,.hljs-template-variable,.hljs-variable{color:#ffa07a}
    .hljs-attribute,.hljs-built_in,.hljs-link,.hljs-literal,.hljs-meta,.hljs-number,.hljs-params,.hljs-type{color:#f5ab35}
    .hljs-addition,.hljs-bullet,.hljs-string,.hljs-symbol{color:#abe338}
    .hljs-keyword,.hljs-selector-tag,.hljs-section,.hljs-title{color:#00e0e0}
    .hljs-emphasis{font-style:italic}
    .hljs-strong{font-weight:700}
  `,
  
  // ---------------------------------------------------------------------------
  // CUSTOM SCHOLARLY THEMES
  // ---------------------------------------------------------------------------
  
  'scholarly': `
    .hljs{background:#faf9f7;color:#2c2c2c}
    .hljs-keyword,.hljs-selector-tag,.hljs-built_in{color:#8b5a2b;font-weight:600}
    .hljs-name,.hljs-tag{color:#6d5a43;font-weight:600}
    .hljs-string,.hljs-template-tag,.hljs-template-variable,.hljs-addition{color:#2e7d32}
    .hljs-title,.hljs-section,.hljs-title.function_,.hljs-title.class_{color:#5d4037;font-weight:600}
    .hljs-attribute,.hljs-literal,.hljs-type{color:#1565c0}
    .hljs-comment,.hljs-quote,.hljs-deletion,.hljs-meta{color:#78909c;font-style:italic}
    .hljs-number{color:#c62828}
    .hljs-regexp,.hljs-link{color:#ad1457}
    .hljs-selector-id,.hljs-selector-class{color:#6a1b9a}
    .hljs-symbol,.hljs-bullet,.hljs-subst{color:#e65100}
    .hljs-variable,.hljs-property{color:#4527a0}
    .hljs-params{color:#37474f}
    .hljs-emphasis{font-style:italic}
    .hljs-strong{font-weight:bold}
  `,
  
  'scholarly-dark': `
    .hljs{background:#1e1e1e;color:#d4d4d4}
    .hljs-keyword,.hljs-selector-tag,.hljs-built_in{color:#c9a26d;font-weight:600}
    .hljs-name,.hljs-tag{color:#9cdcfe;font-weight:600}
    .hljs-string,.hljs-template-tag,.hljs-template-variable,.hljs-addition{color:#6a9955}
    .hljs-title,.hljs-section,.hljs-title.function_,.hljs-title.class_{color:#dcdcaa;font-weight:600}
    .hljs-attribute,.hljs-literal,.hljs-type{color:#4ec9b0}
    .hljs-comment,.hljs-quote,.hljs-deletion,.hljs-meta{color:#6a9955;font-style:italic}
    .hljs-number{color:#b5cea8}
    .hljs-regexp,.hljs-link{color:#d16969}
    .hljs-selector-id,.hljs-selector-class{color:#c586c0}
    .hljs-symbol,.hljs-bullet,.hljs-subst{color:#ce9178}
    .hljs-variable,.hljs-property{color:#9cdcfe}
    .hljs-params{color:#9cdcfe}
    .hljs-emphasis{font-style:italic}
    .hljs-strong{font-weight:bold}
  `,
};

// Theme metadata for UI
const THEME_INFO = {
  // Dark themes
  'atom-one-dark':     { name: 'Atom One Dark',      dark: true },
  'github-dark':       { name: 'GitHub Dark',        dark: true },
  'github-dark-dimmed':{ name: 'GitHub Dark Dimmed', dark: true },
  'monokai':           { name: 'Monokai',            dark: true },
  'monokai-sublime':   { name: 'Monokai Sublime',    dark: true },
  'dracula':           { name: 'Dracula',            dark: true },
  'nord':              { name: 'Nord',               dark: true },
  'tokyo-night-dark':  { name: 'Tokyo Night',        dark: true },
  'vs2015':            { name: 'VS 2015',            dark: true },
  'one-dark':          { name: 'One Dark',           dark: true },
  'obsidian':          { name: 'Obsidian',           dark: true },
  'stackoverflow-dark':{ name: 'Stack Overflow Dark',dark: true },
  'a11y-dark':         { name: 'A11y Dark',          dark: true },
  'scholarly-dark':    { name: 'Scholarly Dark',     dark: true },
  // Light themes
  'atom-one-light':    { name: 'Atom One Light',     dark: false },
  'github':            { name: 'GitHub',             dark: false },
  'vs':                { name: 'Visual Studio',      dark: false },
  'xcode':             { name: 'Xcode',              dark: false },
  'intellij-light':    { name: 'IntelliJ Light',     dark: false },
  'stackoverflow-light':{ name: 'Stack Overflow Light', dark: false },
  'a11y-light':        { name: 'A11y Light',         dark: false },
  'scholarly':         { name: 'Scholarly',          dark: false },
};

// =============================================================================
// COMPONENT STYLES
// =============================================================================

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Crimson+Pro:ital,wght@0,400;0,600;1,400&family=JetBrains+Mono:wght@400;500&display=swap');
  
  :host {
    display: block;
    font-family: 'Crimson Pro', Georgia, serif;
    font-size: 16px;
    color: #2c2c2c;
  }
  
  .container {
    border: 1px solid var(--border-color, #d4d0c8);
    background: var(--container-bg, #fff);
    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
    overflow: hidden;
  }
  
  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5rem 0.75rem;
    background: var(--header-bg, #f5f4f2);
    border-bottom: 1px solid var(--border-color, #d4d0c8);
    min-height: 36px;
  }
  
  .title-section {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    min-width: 0;
    flex: 1;
  }
  
  .collapse-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    padding: 0;
    border: none;
    background: transparent;
    color: var(--text-muted, #666);
    cursor: pointer;
    transition: transform 0.2s ease, color 0.15s;
    flex-shrink: 0;
  }
  
  .collapse-btn:hover {
    color: var(--accent-color, #8b7355);
  }
  
  .collapse-btn svg {
    width: 14px;
    height: 14px;
  }
  
  .collapse-btn.collapsed {
    transform: rotate(-90deg);
  }
  
  .title {
    font-weight: 600;
    font-size: 0.9rem;
    color: var(--text-primary, #2c2c2c);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  .language-badge {
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.65rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 0.15rem 0.4rem;
    background: var(--badge-bg, #e8e5e0);
    color: var(--text-muted, #666);
    border-radius: 2px;
    flex-shrink: 0;
  }
  
  .actions {
    display: flex;
    gap: 0.25rem;
    flex-shrink: 0;
  }
  
  .action-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    padding: 0;
    border: 1px solid transparent;
    background: transparent;
    color: var(--text-muted, #888);
    cursor: pointer;
    transition: all 0.15s;
    border-radius: 2px;
  }
  
  .action-btn:hover {
    border-color: var(--border-color, #c8c4bc);
    color: var(--accent-color, #8b7355);
    background: var(--action-hover-bg, rgba(139, 115, 85, 0.05));
  }
  
  .action-btn.success {
    color: #5a8a5a;
    border-color: #5a8a5a;
    background: rgba(90, 138, 90, 0.1);
  }
  
  .action-btn svg {
    width: 16px;
    height: 16px;
  }
  
  .code-wrapper {
    position: relative;
    overflow: hidden;
    transition: max-height 0.3s ease;
  }
  
  .code-wrapper.collapsed {
    max-height: 0 !important;
  }
  
  .code-scroller {
    overflow: auto;
    max-height: var(--max-height, none);
  }
  
  .code-container {
    display: flex;
    min-width: fit-content;
  }
  
  .line-numbers {
    display: none;
  }
  
  .code-block {
    flex: 1;
    margin: 0;
    padding: 0;
    overflow-x: auto;
    font-family: 'JetBrains Mono', monospace;
    font-size: var(--code-font-size, 0.8rem);
    line-height: var(--line-height, 1.6);
    tab-size: var(--tab-size, 4);
    -moz-tab-size: var(--tab-size, 4);
  }
  
  .code-block.wrap {
    white-space: pre-wrap;
    word-break: break-word;
  }
  
  .code-block.wrap .line {
    white-space: pre-wrap;
    word-break: break-word;
  }
  
  .code-block code {
    display: block;
    padding: 1rem 0;
    font-family: inherit;
    font-size: inherit;
    line-height: inherit;
    background: transparent !important;
  }
  
  .code-block .line-row {
    display: flex;
    align-items: stretch;
  }
  
  .code-block .line-number {
    flex-shrink: 0;
    width: var(--line-number-width, 3rem);
    padding: 0 0.75rem;
    padding-top: 0;
    text-align: right;
    user-select: none;
    background: var(--line-numbers-bg, #f8f7f5);
    border-right: 1px solid var(--line-numbers-border, #e8e5e0);
    color: var(--line-numbers-color, #999);
    display: flex;
    align-items: flex-start;
    justify-content: flex-end;
    line-height: calc(var(--code-font-size, 0.8rem) * var(--line-height, 1.6));
  }
  
  .code-block .line-number.highlighted {
    background: var(--highlight-line-gutter, rgba(139, 115, 85, 0.15));
    color: var(--line-numbers-highlight-color, #666);
  }
  
  .code-block .line {
    flex: 1;
    padding: 0 1rem;
    min-height: calc(var(--code-font-size, 0.8rem) * var(--line-height, 1.6));
    line-height: calc(var(--code-font-size, 0.8rem) * var(--line-height, 1.6));
  }
  
  .code-block .line.highlighted {
    background: var(--highlight-line-bg, rgba(139, 115, 85, 0.1));
  }
  
  .code-block .line-row.line-gap {
    height: calc(var(--code-font-size, 0.8rem) * var(--line-height, 1.6) * 0.75);
    border-top: 1px dashed var(--border-color, #c8c4bc);
    border-bottom: 1px dashed var(--border-color, #c8c4bc);
    margin: 0.25rem 0;
  }
  
  .code-block .line-row.line-gap .line-number {
    color: var(--text-muted, #bbb);
    text-align: center;
  }
  
  .loading, .error {
    padding: 2rem;
    text-align: center;
    font-style: italic;
  }
  
  .loading {
    color: var(--text-muted, #888);
  }
  
  .error {
    color: #a85454;
    background: #fdf6f6;
  }
  
  .footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.35rem 0.75rem;
    background: var(--footer-bg, #faf9f7);
    border-top: 1px solid var(--border-color, #e8e5e0);
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.65rem;
    color: var(--text-muted, #999);
  }
  
  .stats {
    display: flex;
    gap: 1rem;
  }
  
  .file-size {
    color: var(--text-muted, #888);
  }
  
  /* Custom scrollbar */
  .code-scroller::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  
  .code-scroller::-webkit-scrollbar-track {
    background: var(--scrollbar-track, #f5f4f2);
  }
  
  .code-scroller::-webkit-scrollbar-thumb {
    background: var(--scrollbar-thumb, #c8c4bc);
    border-radius: 4px;
  }
  
  .code-scroller::-webkit-scrollbar-thumb:hover {
    background: var(--scrollbar-thumb-hover, #a8a49c);
  }
  
  .code-scroller::-webkit-scrollbar-corner {
    background: var(--scrollbar-track, #f5f4f2);
  }
  
  /* Dark theme adjustments for chrome */
  :host(.theme-dark) .container {
    --border-color: #3e3e3e;
    --container-bg: #1e1e1e;
    --header-bg: #2d2d2d;
    --footer-bg: #252525;
    --badge-bg: #3e3e3e;
    --text-primary: #d4d4d4;
    --text-muted: #808080;
    --accent-color: #c9a26d;
    --action-hover-bg: rgba(201, 162, 109, 0.1);
    --line-numbers-bg: #252525;
    --line-numbers-border: #3e3e3e;
    --line-numbers-color: #6e6e6e;
    --highlight-line-gutter: rgba(139, 115, 85, 0.2);
    --highlight-line-bg: rgba(139, 115, 85, 0.15);
    --scrollbar-track: #2d2d2d;
    --scrollbar-thumb: #4e4e4e;
    --scrollbar-thumb-hover: #5e5e5e;
  }
  
  /* Touch device adjustments */
  @media (hover: none) {
    .action-btn:hover {
      border-color: transparent;
      color: var(--text-muted, #888);
      background: transparent;
    }
    
    .action-btn:active {
      border-color: var(--border-color, #c8c4bc);
      color: var(--accent-color, #8b7355);
      background: var(--action-hover-bg, rgba(139, 115, 85, 0.05));
    }
    
    .collapse-btn:hover {
      color: var(--text-muted, #666);
    }
    
    .collapse-btn:active {
      color: var(--accent-color, #8b7355);
    }
  }
`;

// Icons as SVG strings
const ICONS = {
  copy: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>',
  check: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>',
  chevron: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>',
};

// =============================================================================
// COMPONENT CLASS
// =============================================================================

class CodeDisplay extends HTMLElement {
  static get observedAttributes() {
    return [
      'src', 'language', 'theme', 'tab-size', 'line-numbers', 'gutter-width',
      'highlight-lines', 'show-lines', 'start-line', 'max-height', 'collapsible',
      'collapsed', 'copy-button', 'title', 'wrap'
    ];
  }

  /**
   * Get list of available theme names
   * @returns {string[]}
   */
  static getAvailableThemes() {
    return Object.keys(HLJS_THEMES);
  }

  /**
   * Get theme metadata
   * @returns {Object}
   */
  static getThemeInfo() {
    return { ...THEME_INFO };
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this._code = '';
    this._language = '';
    this._detectedLanguage = '';
    this._isCollapsed = false;
    this._currentTheme = null;
    this._inlineContent = null; // Store inline content before render
  }

  connectedCallback() {
    this._isCollapsed = this.hasAttribute('collapsed');
    
    // Capture inline content BEFORE render() replaces innerHTML
    // HTML content should be escaped (e.g., &lt;div&gt;) to avoid browser parsing
    if (!this.src) {
      const rawContent = this.innerHTML;
      if (rawContent && rawContent.trim()) {
        this._inlineContent = this._decodeAndCleanContent(rawContent);
      }
    }
    
    this.render();
    // Theme is now applied directly in render()
    
    if (this.src) {
      this.loadFile(this.src);
    } else if (this._inlineContent) {
      // Use inline content
      this._code = this._inlineContent;
      this.highlightCode();
    }
  }
  
  /**
   * Decode HTML entities and clean up inline content
   * Handles content with escaped HTML entities (e.g., &lt;div&gt;)
   */
  _decodeAndCleanContent(html) {
    // Decode HTML entities first (e.g., &lt; -> <, &gt; -> >, &amp; -> &)
    const textarea = document.createElement('textarea');
    textarea.innerHTML = html;
    let content = textarea.value;
    
    // Split into lines
    let lines = content.split('\n');
    
    // Remove leading empty lines
    while (lines.length > 0 && lines[0].trim() === '') {
      lines.shift();
    }
    
    // Remove trailing empty lines
    while (lines.length > 0 && lines[lines.length - 1].trim() === '') {
      lines.pop();
    }
    
    if (lines.length === 0) {
      return '';
    }
    
    // Find the indentation of the first line (which sets the baseline)
    const firstLineMatch = lines[0].match(/^(\s*)/);
    const baseIndent = firstLineMatch ? firstLineMatch[1].length : 0;
    
    // Remove the base indentation from all lines
    if (baseIndent > 0) {
      lines = lines.map(line => {
        // Only remove up to baseIndent characters of whitespace
        // This preserves relative indentation within the code
        if (line.length === 0) return line;
        
        let removeCount = 0;
        for (let i = 0; i < Math.min(baseIndent, line.length); i++) {
          if (line[i] === ' ' || line[i] === '\t') {
            removeCount++;
          } else {
            break;
          }
        }
        return line.slice(removeCount);
      });
    }
    
    return lines.join('\n');
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;

    if (name === 'src') {
      this.loadFile(newValue);
    } else if (name === 'theme') {
      this._applyTheme(newValue);
      if (this._code) {
        this.highlightCode();
      }
    } else if (name === 'collapsed') {
      this._isCollapsed = this.hasAttribute('collapsed');
      this.updateCollapseState();
    } else if (name === 'tab-size') {
      const container = this.shadowRoot.querySelector('.container');
      if (container) {
        container.style.setProperty('--tab-size', this.tabSize);
      }
    } else if (name === 'max-height') {
      const scroller = this.shadowRoot.querySelector('.code-scroller');
      if (scroller) {
        scroller.style.maxHeight = this.maxHeight || '';
      }
    } else if (name === 'wrap') {
      const codeBlock = this.shadowRoot.querySelector('.code-block');
      if (codeBlock) {
        codeBlock.classList.toggle('wrap', this.wrapLines);
      }
    } else if (name === 'line-numbers' || name === 'highlight-lines' || name === 'show-lines' || name === 'gutter-width') {
      if (this._code) {
        this.highlightCode();
      }
    } else if (this._code) {
      this.highlightCode();
    }
  }

  // === Getters for attributes ===

  get src() {
    return this.getAttribute('src') || '';
  }

  get language() {
    return this.getAttribute('language') || '';
  }

  get theme() {
    return this.getAttribute('theme') || 'scholarly';
  }

  get tabSize() {
    return parseInt(this.getAttribute('tab-size'), 10) || 4;
  }

  get showLineNumbers() {
    return this.hasAttribute('line-numbers');
  }

  get gutterWidth() {
    const val = this.getAttribute('gutter-width');
    if (val === null || val === '' || val === 'auto') return null;
    return parseInt(val, 10) || null;
  }

  get highlightLines() {
    const attr = this.getAttribute('highlight-lines') || '';
    return this.parseLineRanges(attr);
  }

  get showLines() {
    const attr = this.getAttribute('show-lines') || '';
    return this.parseLineRanges(attr);
  }

  get startLine() {
    return parseInt(this.getAttribute('start-line'), 10) || 1;
  }

  get maxHeight() {
    return this.getAttribute('max-height') || null;
  }

  get isCollapsible() {
    return this.hasAttribute('collapsible');
  }

  get showCopyButton() {
    return this.hasAttribute('copy-button');
  }

  get customTitle() {
    return this.getAttribute('title') || '';
  }

  get wrapLines() {
    return this.hasAttribute('wrap');
  }

  // === Theme handling ===

  _applyTheme(themeName) {
    if (this._currentTheme === themeName) return;
    this._currentTheme = themeName;
    
    // Get or create the theme style element
    let themeStyle = this.shadowRoot.querySelector('#hljs-theme-style');
    if (!themeStyle) {
      themeStyle = document.createElement('style');
      themeStyle.id = 'hljs-theme-style';
      this.shadowRoot.appendChild(themeStyle);
    }
    
    // Apply the theme CSS
    const themeCSS = HLJS_THEMES[themeName] || HLJS_THEMES['scholarly'];
    themeStyle.textContent = themeCSS;
    
    // Update dark/light mode class on host for chrome styling
    const themeInfo = THEME_INFO[themeName];
    const isDark = themeInfo ? themeInfo.dark : false;
    this.classList.toggle('theme-dark', isDark);
  }

  // === Public API ===

  /**
   * Load code from a URL
   * @param {string} url - URL of the file to load
   */
  async loadFile(url) {
    if (!url) return;

    this.showLoading();

    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const code = await response.text();

      // Auto-detect language from extension
      const ext = url.split('.').pop()?.toLowerCase() || '';
      this._detectedLanguage = EXTENSION_MAP[ext] || '';

      this._code = code;
      await this.highlightCode();

      this.dispatchEvent(new CustomEvent('code-loaded', {
        detail: { url, code, language: this.effectiveLanguage }
      }));
    } catch (error) {
      this.showError(`Failed to load: ${error.message}`);
      this.dispatchEvent(new CustomEvent('code-error', {
        detail: { url, error: error.message }
      }));
    }
  }

  /**
   * Set code content directly
   * @param {string} code - The code to display
   * @param {string} [language] - Language for highlighting
   */
  async setCode(code, language = '') {
    this._code = code;
    if (language) {
      this._detectedLanguage = language;
    }
    await this.highlightCode();
  }

  /**
   * Get the current code content
   * @returns {string}
   */
  getCode() {
    return this._code;
  }

  /**
   * Copy code to clipboard
   */
  async copyToClipboard() {
    try {
      await navigator.clipboard.writeText(this._code);
      this.showCopySuccess();
      this.dispatchEvent(new CustomEvent('code-copied', {
        detail: { code: this._code }
      }));
      return true;
    } catch (error) {
      console.error('Failed to copy:', error);
      return false;
    }
  }

  /**
   * Expand the code block
   */
  expand() {
    if (this._isCollapsed) {
      this._isCollapsed = false;
      this.removeAttribute('collapsed');
      this.updateCollapseState();
    }
  }

  /**
   * Collapse the code block
   */
  collapse() {
    if (!this._isCollapsed && this.isCollapsible) {
      this._isCollapsed = true;
      this.setAttribute('collapsed', '');
      this.updateCollapseState();
    }
  }

  /**
   * Re-render with current settings
   */
  refresh() {
    this.highlightCode();
  }

  // === End Public API ===

  get effectiveLanguage() {
    return this.language || this._detectedLanguage || 'plaintext';
  }

  parseLineRanges(str) {
    if (!str) return new Set();

    const lines = new Set();
    const parts = str.split(',');

    for (const part of parts) {
      const trimmed = part.trim();
      if (trimmed.includes('-')) {
        const [start, end] = trimmed.split('-').map(n => parseInt(n, 10));
        if (!isNaN(start) && !isNaN(end)) {
          for (let i = start; i <= end; i++) {
            lines.add(i);
          }
        }
      } else {
        const num = parseInt(trimmed, 10);
        if (!isNaN(num)) {
          lines.add(num);
        }
      }
    }

    return lines;
  }

  async highlightCode() {
    if (!this._code) return;

    const codeWrapper = this.shadowRoot.querySelector('.code-wrapper');
    if (!codeWrapper) return;

    const lang = this.effectiveLanguage;
    const lines = this._code.split('\n');
    const startLine = this.startLine;
    const showLinesSet = this.showLines;
    const hasShowFilter = showLinesSet.size > 0;

    // Highlight each line individually
    const allLines = lines.map((line, i) => {
      const lineNum = startLine + i;
      let highlighted;
      try {
        if (lang && lang !== 'plaintext' && hljs.getLanguage(lang)) {
          highlighted = hljs.highlight(line, { language: lang, ignoreIllegals: true }).value;
        } else {
          highlighted = hljs.highlightAuto(line).value;
        }
      } catch (e) {
        highlighted = this.escapeHtml(line);
      }
      return { lineNum, highlighted };
    });

    // Filter to only show specified lines if show-lines attribute is set
    const filteredLines = hasShowFilter ?
      allLines.filter(({ lineNum }) => showLinesSet.has(lineNum)) :
      allLines;

    this.renderCode(filteredLines);
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  renderCode(linesWithNumbers) {
    const highlightSet = this.highlightLines;
    const totalLines = linesWithNumbers.length;
    const showLineNums = this.showLineNumbers;

    // Calculate line number width
    let lineNumWidth;
    if (this.gutterWidth !== null) {
      lineNumWidth = (this.gutterWidth * 0.6) + 1;
    } else {
      const maxLineNum = linesWithNumbers.length > 0 ?
        Math.max(...linesWithNumbers.map(l => l.lineNum)) :
        1;
      const digits = String(maxLineNum).length;
      lineNumWidth = (digits * 0.6) + 1;
    }

    // Build rows with line numbers and code together
    let rowsHtml = '';
    let prevLineNum = null;

    for (let i = 0; i < linesWithNumbers.length; i++) {
      const { lineNum, highlighted } = linesWithNumbers[i];
      const isHighlighted = highlightSet.has(lineNum);

      // Check if there's a gap (non-contiguous lines)
      if (prevLineNum !== null && lineNum > prevLineNum + 1) {
        rowsHtml += `<div class="line-row line-gap">`;
        if (showLineNums) {
          rowsHtml += `<span class="line-number">⋮</span>`;
        }
        rowsHtml += `<span class="line"></span></div>`;
      }

      // Build the row
      rowsHtml += `<div class="line-row">`;
      if (showLineNums) {
        rowsHtml += `<span class="line-number${isHighlighted ? ' highlighted' : ''}">${lineNum}</span>`;
      }
      rowsHtml += `<span class="line${isHighlighted ? ' highlighted' : ''}">${highlighted || ' '}</span>`;
      rowsHtml += `</div>`;

      prevLineNum = lineNum;
    }

    // Update DOM
    const codeBlock = this.shadowRoot.querySelector('.code-block');
    const languageBadge = this.shadowRoot.querySelector('.language-badge');
    const footer = this.shadowRoot.querySelector('.footer');

    if (codeBlock) {
      codeBlock.innerHTML = `<code class="hljs">${rowsHtml}</code>`;
      codeBlock.classList.toggle('wrap', this.wrapLines);
      codeBlock.style.setProperty('--line-number-width', `${lineNumWidth}rem`);
    }

    if (languageBadge) {
      languageBadge.textContent = this.effectiveLanguage;
    }

    if (footer) {
      const linesText = totalLines === 1 ? '1 line' : `${totalLines} lines`;
      const sizeText = this.formatFileSize(new Blob([this._code]).size);
      footer.querySelector('.line-count').textContent = linesText;
      footer.querySelector('.file-size').textContent = sizeText;
    }
  }

  formatFileSize(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  showLoading() {
    const codeScroller = this.shadowRoot.querySelector('.code-scroller');
    if (codeScroller) {
      codeScroller.innerHTML = `
        <pre class="code-block"><code class="hljs"><div class="loading">Loading code...</div></code></pre>
      `;
    }
  }

  showError(message) {
    const codeScroller = this.shadowRoot.querySelector('.code-scroller');
    if (codeScroller) {
      codeScroller.innerHTML = `
        <pre class="code-block"><code class="hljs"><div class="error">${this.escapeHtml(message)}</div></code></pre>
      `;
    }
  }

  showCopySuccess() {
    const copyBtn = this.shadowRoot.querySelector('.copy-btn');
    if (copyBtn) {
      copyBtn.innerHTML = ICONS.check;
      copyBtn.classList.add('success');
      setTimeout(() => {
        copyBtn.innerHTML = ICONS.copy;
        copyBtn.classList.remove('success');
      }, 2000);
    }
  }

  updateCollapseState() {
    const collapseBtn = this.shadowRoot.querySelector('.collapse-btn');
    const codeWrapper = this.shadowRoot.querySelector('.code-wrapper');
    const footer = this.shadowRoot.querySelector('.footer');

    if (collapseBtn) {
      collapseBtn.classList.toggle('collapsed', this._isCollapsed);
    }
    if (codeWrapper) {
      codeWrapper.classList.toggle('collapsed', this._isCollapsed);
    }
    if (footer) {
      footer.style.display = this._isCollapsed ? 'none' : '';
    }
  }

  toggleCollapse() {
    if (this._isCollapsed) {
      this.expand();
    } else {
      this.collapse();
    }
  }

  getDisplayTitle() {
    if (this.customTitle) return this.customTitle;
    if (this.src) {
      const url = this.src;
      const parts = url.split('/');
      return parts[parts.length - 1] || url;
    }
    return 'Code';
  }

  render() {
    const maxHeightStyle = this.maxHeight ? `max-height: ${this.maxHeight};` : '';
    
    // Get the theme CSS - include it directly in the initial render
    const themeName = this.theme;
    const themeCSS = HLJS_THEMES[themeName] || HLJS_THEMES['scholarly'];
    const themeInfo = THEME_INFO[themeName];
    const isDark = themeInfo ? themeInfo.dark : false;
    
    // Update dark/light mode class on host
    this.classList.toggle('theme-dark', isDark);
    this._currentTheme = themeName;

    this.shadowRoot.innerHTML = `
      <style>${STYLES}</style>
      <style id="hljs-theme-style">${themeCSS}</style>
      
      <div class="container" style="--tab-size: ${this.tabSize}; --max-height: ${this.maxHeight || 'none'};">
        <div class="header">
          <div class="title-section">
            ${this.isCollapsible ? `
              <button class="collapse-btn${this._isCollapsed ? ' collapsed' : ''}" 
                      title="${this._isCollapsed ? 'Expand' : 'Collapse'}">
                ${ICONS.chevron}
              </button>
            ` : ''}
            <span class="title">${this.escapeHtml(this.getDisplayTitle())}</span>
            <span class="language-badge">${this.effectiveLanguage}</span>
          </div>
          <div class="actions">
            ${this.showCopyButton ? `
              <button class="action-btn copy-btn" title="Copy to clipboard">
                ${ICONS.copy}
              </button>
            ` : ''}
          </div>
        </div>
        
        <div class="code-wrapper${this._isCollapsed ? ' collapsed' : ''}">
          <div class="code-scroller" style="${maxHeightStyle}">
            <pre class="code-block${this.wrapLines ? ' wrap' : ''}"><code class="hljs"></code></pre>
          </div>
        </div>
        
        <div class="footer" style="${this._isCollapsed ? 'display: none;' : ''}">
          <div class="stats">
            <span class="line-count">0 lines</span>
            <span class="file-size">0 B</span>
          </div>
        </div>
      </div>
    `;

    // Event listeners
    if (this.isCollapsible) {
      const collapseBtn = this.shadowRoot.querySelector('.collapse-btn');
      collapseBtn?.addEventListener('click', (e) => {
        e.stopPropagation();
        this.toggleCollapse();
      });

      const header = this.shadowRoot.querySelector('.header');
      header?.addEventListener('click', (e) => {
        if (!e.target.closest('.action-btn')) {
          this.toggleCollapse();
        }
      });
      header.style.cursor = 'pointer';
    }

    if (this.showCopyButton) {
      const copyBtn = this.shadowRoot.querySelector('.copy-btn');
      copyBtn?.addEventListener('click', (e) => {
        e.stopPropagation();
        this.copyToClipboard();
      });
    }
  }
}

customElements.define('code-display', CodeDisplay);

// Export for module usage
export { CodeDisplay, EXTENSION_MAP, HLJS_THEMES, THEME_INFO };
export default CodeDisplay;
